/**
 * ConnectorCallbackPage
 * 
 * Shown inside the OAuth popup after Microsoft redirects back.
 * Reads standard OAuth2 error params from the URL, displays success or error,
 * and auto-closes after a short delay on success.
 */
import { useEffect, useState } from "react";
import { CheckCircle, XCircle, ArrowsClockwise } from "@phosphor-icons/react";

export default function ConnectorCallbackPage() {
  const params      = new URLSearchParams(window.location.search);
  const error       = params.get("error");
  const description = params.get("error_description");
  const connector   = params.get("connector") || "connector";

  const [countdown, setCountdown] = useState(4);

  const label = connector === "outlook" ? "Outlook Email"
              : connector === "onedrive" ? "OneDrive Files"
              : "Connector";

  useEffect(() => {
    if (error) return; // don't auto-close on error

    const timer = setInterval(() => {
      setCountdown(n => {
        if (n <= 1) {
          clearInterval(timer);
          window.close();
        }
        return n - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [error]);

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, sans-serif",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      margin: 0,
      background: "#f8fafc",
    }}>
      <div style={{
        background: "white",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: "40px 48px",
        textAlign: "center",
        maxWidth: 380,
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
      }}>
        {error ? (
          <>
            <XCircle size={48} color="#ef4444" weight="fill" style={{ marginBottom: 16 }} />
            <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
              Connection failed
            </h2>
            <p style={{ margin: "0 0 8px", fontSize: 13, color: "#64748b" }}>
              <strong>{label}</strong> could not be authorized.
            </p>
            <p style={{
              margin: "12px 0 0",
              fontSize: 12,
              color: "#94a3b8",
              background: "#f1f5f9",
              borderRadius: 6,
              padding: "8px 12px",
              textAlign: "left",
              wordBreak: "break-word",
            }}>
              {description
                ? description.replace(/\+/g, " ")
                : error}
            </p>
            <button
              onClick={() => window.close()}
              style={{
                marginTop: 20,
                padding: "8px 20px",
                fontSize: 13,
                fontWeight: 600,
                background: "#0f172a",
                color: "white",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Close this window
            </button>
          </>
        ) : (
          <>
            <CheckCircle size={48} color="#22c55e" weight="fill" style={{ marginBottom: 16 }} />
            <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
              Successfully connected!
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
              <strong>{label}</strong> has been authorized.
            </p>
            <p style={{ margin: "16px 0 0", fontSize: 12, color: "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <ArrowsClockwise size={12} />
              Closing in {countdown}s…
            </p>
            <button
              onClick={() => window.close()}
              style={{
                marginTop: 12,
                padding: "8px 20px",
                fontSize: 13,
                fontWeight: 600,
                background: "#f1f5f9",
                color: "#475569",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Close now
            </button>
          </>
        )}
      </div>
    </div>
  );
}
